import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { differenceInCalendarDays } from 'date-fns';

type SupportedCurrency = 'EUR' | 'GBP';

const CURRENCY_FORMAT_OPTIONS: Record<SupportedCurrency, { locale: string; currency: SupportedCurrency }> = {
  EUR: {
    locale: 'en-IE',
    currency: 'EUR',
  },
  GBP: {
    locale: 'en-GB',
    currency: 'GBP',
  },
};

function isSupportedCurrency(currency: string): currency is SupportedCurrency {
    return currency in CURRENCY_FORMAT_OPTIONS;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov'];

/**
 * Determines whether a URL points to a video file by inspecting the file extension.
 */
export function isVideoUrl(url: string): boolean {
    try {
        const pathname = new URL(url).pathname.toLowerCase();
        return VIDEO_EXTENSIONS.some(ext => pathname.endsWith(ext));
    } catch {
        return false;
    }
}

export function formatCurrency(amount: number, currency: string = 'EUR', locale?: string): string {
    if (!isSupportedCurrency(currency)) {
        return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
    }

    const formatOptions = CURRENCY_FORMAT_OPTIONS[currency];

    return new Intl.NumberFormat(locale ?? formatOptions.locale, {
        style: 'currency',
        currency: formatOptions.currency,
    }).format(amount);
}

export function getRentalDayCount(startDate: Date, endDate: Date): number {
    return Math.max(1, differenceInCalendarDays(endDate, startDate));
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-IE', {
        dateStyle: 'medium',
        ...options,
    }).format(dateObj);
}

export function formatDateTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-IE', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(dateObj);
}
